import React from 'react';
import {
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, Today } from '@mui/icons-material';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { CalendarView, DATE_FORMAT } from './CalendarWrapper';
import DatesCarousel from '../DatesCarousel';
import { useAuth } from '../../session/InternalAuthProvider';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { AccountData, OrderPageData, OrderPageTypes } from '../../api';
import { getOrderPages } from '../../common/AccountUtils';
import { getInitialCurrentDate } from '../CalendarContext/getInitialCurrentDate';
import TTLLocalStorage, { WEBOOK_SAVED_CURRENT_DATE } from '../../common/TTLLocalStorage';
import { UseAuthPermissions } from '../../session/UseAuthPermissions';
import { UsersMultiSelect } from '../components/UsersMultiSelect';
import { CompactStats } from './CompactStats';

type CalendarToolbarProps = {
  previousNextCallbacks: {
    previous: any;
    next: any;
  };
  setNoteDialogOpen: (isNoteDialogOpen: boolean) => void;
  setShiftDialogOpen: (isShiftDialogIsOpen: boolean) => void;
};

export const CalendarToolbar = ({
  previousNextCallbacks,
  setNoteDialogOpen,
  setShiftDialogOpen,
}: CalendarToolbarProps) => {
  const theme = useTheme();
  const { authState } = useAuth();
  const { readOnly, isAtLeastSupervisor } = UseAuthPermissions();
  const allowCalendarCondensedMode = useFeatureFlagEnabled('allow-calendar-condensed-mode');

  const {
    openEditOrderDrawer,
    users,
    selectedUsersIds,
    setSelectedUsersIds,
    openExperienceAvailabilityDrawer,
    setCalendarView,
    setCurrentDate,
    calendarView,
    currentDate,
  } = useCalendar();
  const { isSidebarOpen, setIsSidebarOpen } = useOutletContext<{
    isSidebarOpen: boolean;
    setIsSidebarOpen: (isOpen: boolean) => void;
  }>();
  const setCurrentDateInternal = (newCurrentDate: string) => {
    setCurrentDate(newCurrentDate);
    TTLLocalStorage.setItem(WEBOOK_SAVED_CURRENT_DATE, newCurrentDate, 86400000 * 7);
    const newUrl = `${window.location.origin}/dashboard/calendar/${newCurrentDate}`;
    window.history.pushState(null, '', newUrl);
  };

  const account = authState.account as unknown as AccountData;

  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const { t } = useTranslation();

  const orderPages = getOrderPages(account);

  const onTodayClicked = () => {
    setCurrentDate(getInitialCurrentDate({ calendarView }));
    const newUrl = `${window.location.origin}/dashboard/calendar/`;
    window.history.pushState(null, '', newUrl);
    TTLLocalStorage.removeItem(WEBOOK_SAVED_CURRENT_DATE);
  };
  // 10 is arbitrary, but shouldn't allow condenced mode for customers with too many order pages.
  const hasLessThen10OrderPageAndTheyAreEvents =
    orderPages.filter((x: OrderPageData) => x.order_page_type === OrderPageTypes.EVENT).length < 10 &&
    orderPages.filter((x: OrderPageData) => x.order_page_type === OrderPageTypes.PRIVATE).length === 0;

  return (
    <Grid>
      <Grid container flexDirection="column">
        {!readOnly && <CompactStats />}

        <Grid pt={2} container alignItems="flex-start" gap={1} wrap="nowrap" px={isSmallScreen ? 0 : 6}>
          {isSmallScreen && (
            <Grid>
              <IconButton
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                sx={{ color: (theme) => theme.palette.grey[500] }}>
                <MenuIcon />
              </IconButton>
            </Grid>
          )}

          {calendarView === CalendarView.CONDENSED ? null : isSmallScreen ? null : (
            <Grid item>
              <Button variant="outlined" sx={{ textTransform: 'none', height: '39px' }} onClick={onTodayClicked}>
                <Grid container gap={1} width={'70px'}>
                  <Today style={{ width: '20px' }} />
                  <Typography>{t('calendar.today')}</Typography>
                </Grid>
              </Button>
            </Grid>
          )}
          <Grid>
            <FormControl>
              <Select
                style={{ height: '39px', width: '90px' }}
                className="view-select"
                value={calendarView}
                onChange={(event) => {
                  setCalendarView(event.target.value as CalendarView);
                  if (event.target.value === 'week') {
                    setCurrentDateInternal(moment(currentDate).startOf('week').format(DATE_FORMAT));
                  }
                  if (event.target.value === 'month') {
                    setCurrentDateInternal(moment(currentDate).startOf('month').format(DATE_FORMAT));
                  }
                }}>
                <MenuItem value={CalendarView.DAY}>{t('calendar.view.day')}</MenuItem>
                <MenuItem value={CalendarView.THREE_DAY}>{t('calendar.view.3day')}</MenuItem>
                <MenuItem value={CalendarView.WEEK}>{t('calendar.view.week')}</MenuItem>
                <MenuItem value={CalendarView.MONTH}>{t('calendar.view.month')}</MenuItem>
                {hasLessThen10OrderPageAndTheyAreEvents || allowCalendarCondensedMode ? (
                  <MenuItem value={CalendarView.CONDENSED}>{t('calendar.view.condensed')}</MenuItem>
                ) : null}
              </Select>
            </FormControl>
          </Grid>

          {calendarView === CalendarView.CONDENSED ? null : (
            <Grid item>
              <DatesCarousel
                currentDate={currentDate}
                setCurrentDate={setCurrentDateInternal}
                calendarView={calendarView}
                previousNextCallbacks={previousNextCallbacks}
              />
            </Grid>
          )}
          {calendarView === CalendarView.CONDENSED ? null : isSmallScreen ? (
            <Grid container justifyContent={'flex-end'} px={1}>
              <IconButton
                onClick={onTodayClicked}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}>
                <Today />
              </IconButton>
            </Grid>
          ) : null}
          {isSmallScreen ? null : (
            <UsersMultiSelect
              size="small"
              users={users}
              selectedUsersIds={selectedUsersIds}
              setSelectedUsersIds={setSelectedUsersIds}
              minWidth={120}
            />
          )}
          {!isMediumScreen && (
            <Grid container spacing={1} alignItems="center" justifyContent={'flex-end'}>
              <Grid item>
                <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                  <Button
                    disabled={!isAtLeastSupervisor}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => setNoteDialogOpen(true)}>
                    <Typography> {t('calendar.add_note')}</Typography>
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item>
                <Button variant="outlined" sx={{ textTransform: 'none' }} onClick={() => setShiftDialogOpen(true)}>
                  <Typography>
                    {account.shifts_as_employee_unavailability
                      ? t('calendar.add_employee_unavliability')
                      : t('calendar.add_shift')}
                  </Typography>
                </Button>
              </Grid>
              <Grid item>
                <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                  <Button
                    disabled={!isAtLeastSupervisor}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                    onClick={() => openExperienceAvailabilityDrawer({})}>
                    <Typography>{t('calendar.add_unavailability')}</Typography>
                  </Button>
                </Tooltip>
              </Grid>
              <Grid item>
                <Tooltip title={!isAtLeastSupervisor ? t('noPermissions') : undefined}>
                  <Button
                    disabled={!isAtLeastSupervisor}
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                    onClick={() => openEditOrderDrawer({})}>
                    <Typography>{t('add_order')}</Typography>
                  </Button>
                </Tooltip>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};
